import { motion, useScroll, useTransform } from "framer-motion";
import { Instagram } from "lucide-react";
import { imgSrc } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { RevealLine, ScrollReveal } from "@/components/Reveal";

const DRIVE = "https://lh3.googleusercontent.com/d/";
const ROBIN_IMG = `${DRIVE}1yLVMMdFfbtYA5k5d6-bTQE9Pn6itV-Cd=w2000`;

const FOUNDERS = [
  {
    name: "Victoria Shao",
    role: "Design & Code",
    photo: "https://lh3.googleusercontent.com/d/13rUtWJa0qodi2i0Dn0mMPB4_VXGNU15g=w1200",
    pos: "center 3%",
    alt: "Portrait of Victoria Shao on campus",
    bio: "Hi! I'm Victoria Shao, Student Management Core of Cranbrook Robotics Club. My teammate and I competed at the VEXV5 World Championship. I am also an associate editor photographer for The Crane Clarion, where my photos have been featured in multiple issues. I'm part of the Cranbrook Animal Volunteering Club and have participated in various animal rescue events.\n\nOutside of clubs, I love coding, data science and machine learning, as well as music and figure skating. I'm grateful for the opportunity to build the CKFindingFauna website to share the amazing wildlife around our campus with the community!",
  },
  {
    name: "Ava Phipps",
    role: "Photography & Birds",
    photo: "https://lh3.googleusercontent.com/d/1VrswJ6mJNG-26wM3MAkqId-wUn8YmrBz=w1200",
    pos: "center 5%",
    alt: "Portrait of Ava Phipps holding flowers",
    bio: "Hi! I'm Ava Phipps, and I serve as a co president for CK bee club. I am CKs resident bird watcher, and I'm a fanatic for everything ornithological. I've raised money and advocated for environmentalism since second grade, and I've always loved the natural world. Protecting nature means the world to me, and I'm so happy I had the opportunity to help make this website!",
  },
];

export default function About() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 180]);

  return (
    <div className="min-h-screen" data-testid="about-page">
      <Navbar />

      {/* Robin banner hero */}
      <section className="relative flex h-[62vh] items-center justify-center overflow-hidden" data-testid="about-hero">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img
            src={ROBIN_IMG}
            srcSet="https://lh3.googleusercontent.com/d/1yLVMMdFfbtYA5k5d6-bTQE9Pn6itV-Cd=w900 900w, https://lh3.googleusercontent.com/d/1yLVMMdFfbtYA5k5d6-bTQE9Pn6itV-Cd=w1600 1600w, https://lh3.googleusercontent.com/d/1yLVMMdFfbtYA5k5d6-bTQE9Pn6itV-Cd=w2000 2000w"
            sizes="100vw"
            alt="An American Robin perched in a tree on the Cranbrook campus"
            className="h-[125%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/30" />
        </motion.div>
        <h1
          className="relative text-center font-serif text-[16vw] font-semibold leading-none tracking-tighter text-[#f6f3ea] md:text-[9vw]"
          data-testid="about-title"
        >
          <RevealLine delay={0.2}>About Us<span className="text-primary">.</span></RevealLine>
        </h1>
      </section>

      {/* Origin story */}
      <section className="mx-auto max-w-3xl px-6 py-24 text-center md:py-32" data-testid="about-story">
        <ScrollReveal>
          <h2 className="font-serif text-3xl font-semibold leading-tight tracking-tight md:text-5xl">
            This project all started with two students and a dream.
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={0.12}>
          <p className="mt-10 text-base leading-loose text-foreground/80 md:text-lg">
            During their junior year, Ava Phipps and Victoria Shao applied for the Grimaldi Family Award. With the money they got from the entrepreneurial fund, they were finally able to pursue their passion project: a website that would bring more attention to the diverse wildlife on campus and connect all conservation projects at CK together!
          </p>
        </ScrollReveal>
        <ScrollReveal delay={0.2}>
          <p className="mx-auto mt-10 max-w-2xl border-l-2 border-primary pl-6 text-left font-serif text-lg italic leading-relaxed text-muted-foreground md:text-xl" data-testid="about-thanks">
            Thank you to the Grimaldi Family for making this project possible and contributing to furthering conservation on Cranbrook campus.
          </p>
        </ScrollReveal>
      </section>

      {/* Founders */}
      <section className="mx-auto max-w-6xl px-6 pb-28 md:px-16 md:pb-36" data-testid="about-founders">
        <ScrollReveal className="mb-12 flex items-end justify-between gap-6 border-t border-border pt-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">The founders</p>
            <h2 className="mt-3 font-serif text-4xl font-semibold tracking-tight md:text-5xl">Ava &amp; Victoria</h2>
          </div>
          <p className="hidden max-w-xs text-sm leading-relaxed text-muted-foreground md:block">
            Two juniors, one campus full of wildlife — and one website to share it.
          </p>
        </ScrollReveal>
        <div className="space-y-10">
          {FOUNDERS.map((f, i) => (
            <ScrollReveal key={f.name} delay={i * 0.12}>
              <motion.article
                whileHover={{ y: -6 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="flex flex-col border border-border bg-card shadow-[0_24px_70px_-30px_rgba(20,40,25,0.35)] md:flex-row md:items-stretch"
                data-testid={i === 0 ? "founder-victoria" : "founder-phipps"}
              >
                <div className="shrink-0 overflow-hidden md:w-2/5 lg:w-1/3">
                  <img
                    src={imgSrc(f.photo)}
                    alt={f.alt}
                    style={{ objectPosition: f.pos || "center 20%" }}
                    className="h-72 w-full object-cover md:h-full"
                  />
                </div>
                <div className="flex flex-1 flex-col justify-center p-7 md:p-10">
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">{f.role}</p>
                  <h3 className="mt-2 font-serif text-3xl font-semibold tracking-tight" data-testid={i === 0 ? "founder-name-victoria" : "founder-name-ava"}>
                    {f.name}
                  </h3>
                  <p className="mt-4 whitespace-pre-line text-sm leading-loose text-foreground/80">{f.bio}</p>
                </div>
              </motion.article>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Links strip */}
      <footer className="border-t border-border bg-foreground text-background" data-testid="about-footer">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6 py-10 md:px-16">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-background/50">Links</p>
          <a
            href="https://instagram.com/ck_findingfauna"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-instagram-link"
            className="flex items-center gap-2 text-sm transition-colors duration-300 hover:text-primary"
          >
            <Instagram className="h-4 w-4" />
            @ck_findingfauna
          </a>
        </div>
      </footer>
    </div>
  );
}
